import React, { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { supabase } from '../../utils/supabase';
import { PlayerRecentSessionsCarousel } from '../../components/player/PlayerRecentSessionsCarousel';
import PendingJoinRequestsCard from '../../components/profile/PendingJoinRequestsCard';
import GuestAccountMergesSection from '../../components/guest/GuestAccountMergesSection';
import { getPlayerAvatarPublicUrl } from '../../services/avatarService';

const ProfileOverview = () => {
  const { currentUser } = useOutletContext();
  const [clubCount, setClubCount] = useState(0);
  const [primaryClub, setPrimaryClub] = useState(null);
  const [kpis, setKpis] = useState({ totalGames: 0, wins: 0, losses: 0, winRate: 0, totalSessions: 0 });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  const fullName = [currentUser?.first_name, currentUser?.last_name].filter(Boolean).join(' ') || 'Player';

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.id) { setLoading(false); return; }

      try {
        // Avatar
        if (currentUser.avatar_url) {
          setAvatarUrl(getPlayerAvatarPublicUrl(currentUser.avatar_url));
        } else {
          setAvatarUrl(null);
        }

        // Clubs, KPIs, and session count in parallel
        const [clubsResult, rpcResult, sessionsResult] = await Promise.all([
          supabase
            .from('players_club')
            .select('club_id, clubs:club_id(id, name)')
            .eq('player_id', currentUser.id)
            .eq('is_active', true)
            .order('created_at', { ascending: true }),
          supabase
            .rpc('get_player_game_kpis', { p_player_id: currentUser.id })
            .single(),
          supabase
            .from('session_players')
            .select('session_id', { count: 'exact', head: true })
            .eq('player_id', currentUser.id)
            .neq('status', 'cancelled'),
        ]);

        // Clubs
        const clubsData = clubsResult.data || [];
        setClubCount(clubsData.length);
        if (clubsData.length > 0) {
          setPrimaryClub(clubsData[0]?.clubs?.name || null);
        }

        // Sessions count — count directly from session_players (not game history)
        const sessionCount = sessionsResult.count || 0;

        // KPIs
        const rpcData = rpcResult.data;
        if (!rpcResult.error && rpcData) {
          setKpis({
            totalGames: rpcData.total_games || 0,
            wins: rpcData.wins || 0,
            losses: rpcData.losses || 0,
            winRate: rpcData.win_rate || 0,
            totalSessions: sessionCount,
          });
        } else {
          setKpis(prev => ({ ...prev, totalSessions: sessionCount }));
        }
      } catch (err) {
        console.error('Error loading hub data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser?.id, currentUser?.avatar_url]);

  const initials = [currentUser?.first_name?.[0], currentUser?.last_name?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase();

  return (
    <div className="space-y-4">

      {/* ── Hero banner ── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-900 to-blue-950 text-white shadow-lg">
        {/* Decorative background circles */}
        <div className="absolute -top-10 -right-10 w-52 h-52 rounded-full bg-blue-600/20 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-indigo-500/15 blur-2xl" />

        <div className="relative px-6 py-6 flex items-center gap-5">
          {/* Avatar + change photo */}
          <div className="shrink-0 flex flex-col items-center gap-2">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={fullName}
                className="w-20 h-20 rounded-full object-cover ring-4 ring-white/20 shadow-lg"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-blue-700/60 ring-4 ring-white/20 flex items-center justify-center text-2xl font-bold text-white">
                {initials || '?'}
              </div>
            )}
            <Link
              to="/profile/settings#profile-picture"
              className="text-xs font-medium text-blue-200 hover:text-white underline underline-offset-2 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:rounded"
            >
              Change photo
            </Link>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-white leading-tight truncate">{fullName}</h2>
            {primaryClub && (
              <p className="text-sm text-blue-200 mt-0.5 truncate">{primaryClub}</p>
            )}

            {/* Inline stat pills */}
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-400 uppercase tracking-wide font-medium">Sessions</span>
                <span className="text-sm font-bold text-white">{loading ? '–' : kpis.totalSessions}</span>
              </div>
              <div className="h-4 w-px bg-white/20" />
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-400 uppercase tracking-wide font-medium">Clubs</span>
                <span className="text-sm font-bold text-white">{loading ? '–' : clubCount}</span>
              </div>
              {!loading && kpis.totalGames > 0 && (
                <>
                  <div className="h-4 w-px bg-white/20" />
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-slate-400 uppercase tracking-wide font-medium">Win Rate</span>
                    <span className={`text-sm font-bold ${kpis.winRate >= 50 ? 'text-green-400' : 'text-slate-300'}`}>
                      {kpis.winRate}%
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* W/L mini-row */}
        {!loading && kpis.totalGames > 0 && (
          <div className="relative flex border-t border-white/10">
            {[
              { label: 'Wins', value: kpis.wins, color: 'text-green-400' },
              { label: 'Losses', value: kpis.losses, color: 'text-red-400' },
              { label: 'Sessions', value: kpis.totalSessions, color: 'text-blue-300' },
            ].map((stat, i) => (
              <div key={stat.label} className={`flex-1 py-3 text-center ${i > 0 ? 'border-l border-white/10' : ''}`}>
                <p className={`text-base font-bold ${stat.color}`}>{stat.value}</p>
                <p className="text-xs text-slate-400 mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Recent Activity ── */}
      <PlayerRecentSessionsCarousel
        autoFetch
        currentUser={currentUser}
        fetchLimit={5}
        title="Recent activity"
        viewAllTo="/profile/sessions"
      />

      {/* ── Explore ── */}
      <section className="rounded-2xl bg-surface border border-border shadow-sm px-4 pt-4 pb-4">
        <h3 className="text-xs font-semibold text-text-muted uppercase tracking-widest mb-3">Explore</h3>
        <div className="rounded-xl border border-border overflow-hidden divide-x divide-border">
          <div className="grid grid-cols-4">

            <Link to="/profile/clubs" className="flex flex-col items-center gap-2 px-2 py-4 text-text-primary hover:bg-surface-subtle transition-colors">
              <div className="w-9 h-9 rounded-full bg-info-light flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-info" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <span className="text-xs font-semibold text-center leading-tight text-text-secondary">My Clubs</span>
            </Link>

            <Link to="/profile/games" className="flex flex-col items-center gap-2 px-2 py-4 text-text-primary hover:bg-surface-subtle transition-colors border-l border-border">
              <div className="w-9 h-9 rounded-full bg-success-light flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-semibold text-center leading-tight text-text-secondary">My Games</span>
            </Link>

            <Link to="/profile/matchups" className="flex flex-col items-center gap-2 px-2 py-4 text-text-primary hover:bg-surface-subtle transition-colors border-l border-border">
              <div className="w-9 h-9 rounded-full bg-warning-light flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <span className="text-xs font-semibold text-center leading-tight text-text-secondary">Matchups</span>
            </Link>

            <Link to="/profile/settings" className="flex flex-col items-center gap-2 px-2 py-4 text-text-primary hover:bg-surface-subtle transition-colors border-l border-border">
              <div className="w-9 h-9 rounded-full bg-surface-elevated flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <span className="text-xs font-semibold text-center leading-tight text-text-secondary">My Account</span>
            </Link>

          </div>
        </div>
      </section>

      {/* ── Club Requests ── */}
      <section className="rounded-2xl bg-surface border border-border shadow-sm px-4 pt-4 pb-4">
        <h3 className="text-xs font-semibold text-text-muted uppercase tracking-widest mb-3">Club Requests</h3>
        <PendingJoinRequestsCard currentUser={currentUser} />
      </section>

      {/* ── Guest account linking (when pending or declined merges exist) ── */}
      <GuestAccountMergesSection currentUser={currentUser} profileHubSection />

    </div>
  );
};

export default ProfileOverview;
